import { Design, DraftConfiguration, DraftState, Point } from '@/types';
import { assertDefined } from '@/utils/assertDefined';
import { ensureSizeMm } from '@/model/configuration/convertSize';
import { getCurrentSizeOfConfiguration } from '@/model/configuration/getCurrentSizeOfConfiguration';
import { getDraftConfigurationRestrictForDesign } from '@/model/configuration/getDraftConfigurationRestrictForDesign';
import { getRandomCid } from '@/model/configuration/getRandomCid';
import { isDraftDesignConfiguration } from '@/model/configuration/isDraftConfigurationType';
import { scaleSize } from '@/model/configuration/scaleSize';
import { getScaleForPrintType } from '@/model/design/getScaleForPrintType';
import { getAppearance } from '@/model/draft/getAppearance';
import { getConfigurations } from '@/model/draft/getConfigurations';
import { getPrintAreaForNewConfiguration } from '@/model/draft/getPrintAreaForNewConfiguration';
import { getProductType } from '@/model/draft/getProductType';
import { getPrintAreaSize } from '@/model/printArea/getPrintAreaSize';
import { convertDesignColors } from '@/model/printType/convertDesignColors';
import { getIsSingleConfigurationPrintType } from '@/model/printType/getIsSingleConfigurationPrintType';
import { printTypesForDesign } from '@/model/printType/printTypesForDesign';
import { getIsDieCutStickerAppearance } from '@/model/productType/getIsDieCutStickerAppearance';
import { getDefaultBox } from '@/components/draft/draftState/actions/helper/getDefaultBox';
import { addConfigurationToDraft } from './helper/addConfigurationToDraft';
import { removeConfiguration } from './helper/removeConfiguration';

const STICKER_DEFAULT_SIZE = 100; // mm

export const addDesignAction = (design: Design) => (draft: DraftState) => {
  const appearance = getAppearance(draft);
  const printArea = assertDefined(getPrintAreaForNewConfiguration(draft));
  const printType = printTypesForDesign(design, printArea, getAppearance(draft))[0];
  const designSize = ensureSizeMm(design.size, printType);
  const { width: designWidth, height: designHeight } = designSize;

  let offset: Point;
  let scale: number;

  if (getIsDieCutStickerAppearance(appearance)) {
    offset = { x: 0, y: 0 };

    let size = STICKER_DEFAULT_SIZE;

    // if we have a current configuration then try to match the size
    const currentStickerConfiguration = getConfigurations(draft).find(isDraftDesignConfiguration);
    if (currentStickerConfiguration) {
      const { height: currentDesignHeight, width: currentDesignWidth } =
        getCurrentSizeOfConfiguration(currentStickerConfiguration);
      size = Math.max(currentDesignWidth, currentDesignHeight);
    }

    scale = getScaleForPrintType(
      design,
      printArea,
      printType,
      size / Math.max(designHeight, designWidth)
    );
  } else if (!design.restrictions.allowScale) {
    scale = 1;
    const printAreaSize = getPrintAreaSize(printArea);
    offset = {
      x: (printAreaSize.width - designWidth) / 2,
      y: (printAreaSize.height - designHeight) / 2,
    };
  } else {
    const {
      x: defaultBoxX,
      y: defaultBoxY,
      width: defaultBoxWidth,
      height: defaultBoxHeight,
    } = getDefaultBox(printArea, printType, getProductType(draft));
    const defaultBoxScale = Math.min(
      defaultBoxWidth / designWidth,
      defaultBoxHeight / designHeight
    );
    scale = getScaleForPrintType(design, printArea, printType, defaultBoxScale);
    const { width: scaledDesignWidth } = scaleSize(designSize, scale);
    offset = {
      x: defaultBoxX + defaultBoxWidth / 2 - scaledDesignWidth / 2,
      y: defaultBoxY,
    };
  }

  if (getIsSingleConfigurationPrintType(printType)) {
    // Only one configuration allowed on the same print area for this print type.
    // Do not remove configurations on other print areas/views.
    draft.configurationIds
      .filter(
        (configurationId) => draft.configurations[configurationId]?.printArea.id === printArea.id
      )
      .forEach((configurationId) => removeConfiguration(draft, configurationId));
  }

  const newConfiguration: DraftConfiguration = {
    cid: getRandomCid(),
    type: 'design',
    printArea,
    printType,
    design,
    printColors: convertDesignColors(printType, design.colors),
    rotation: 0,
    offset,
    scale,
    size: designSize,
    restrict: getDraftConfigurationRestrictForDesign(design),
  };

  addConfigurationToDraft(draft, newConfiguration);
};
